import { Node, mergeAttributes } from "@tiptap/core";

export interface AuthenticatedImageAttributes {
  src: string | null;
  folderUuid: string | null;
  alt: string | null;
  title: string | null;
  width: string | null;
  height: string | null;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    authenticatedImage: {
      setImage: (options: Partial<AuthenticatedImageAttributes>) => ReturnType;
    };
  }
}

export const AuthenticatedImageNode = Node.create({
  name: "image",
  group: "block",
  inline: false,
  draggable: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({ src: attributes.src }),
      },
      folderUuid: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-folder-uuid"),
        renderHTML: (attributes) => ({
          "data-folder-uuid": attributes.folderUuid,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({ alt: attributes.alt }),
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => ({ title: attributes.title }),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attributes) => ({ width: attributes.width }),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height"),
        renderHTML: (attributes) => ({ height: attributes.height }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "img[src]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, "data-folder-uuid": folderUuid } = HTMLAttributes;

    // If src is a UUID (not a data URL or http URL), we need to handle it specially
    // For server-side rendering, we'll use a placeholder approach
    const isUuid =
      src &&
      !src.startsWith("data:") &&
      !src.startsWith("http") &&
      src.length > 20;

    if (isUuid) {
      // For SSR, render a placeholder that can be replaced client-side
      return [
        "img",
        mergeAttributes(HTMLAttributes, {
          src: `data:image/svg+xml;base64,${btoa(`
            <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
              <rect width="100%" height="100%" fill="#f8f9fa" stroke="#dee2e6" stroke-width="1"/>
              <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="system-ui" font-size="12" fill="#6c757d">
                Image (${src.substring(0, 8)}...)
              </text>
            </svg>
          `)}`,
          "data-file-uuid": src, // Store the original file UUID for client-side processing
          "data-folder-uuid": folderUuid, // Store the folder UUID for client-side processing
          alt: HTMLAttributes.alt || "Image",
        }),
      ];
    }

    return ["img", mergeAttributes(HTMLAttributes)];
  },

  addCommands() {
    return {
      setImage:
        (options: Partial<AuthenticatedImageAttributes>) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: options,
            })
            .run();
        },
    };
  },
});
